import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NativeTimeInput } from './ui/NativeDateInput';
import { MobileSelect } from './ui/MobileSelect';
import type {
  Team, AgeLevel, ExperienceLevel, Sport,
  FootballOffensiveScheme, FootballDefensiveScheme,
  BasketballOffensiveScheme, BasketballDefensiveScheme,
  SoccerFormation, SoccerStyle,
  BaseballFocus,
  HockeySystem,
  VolleyballOffense, VolleyballDefense,
  LacrosseOffense, LacrosseDefense,
  TennisStyle
} from '../types';
import { SportFormats, AGE_LEVELS, getAgeLevelLabel } from '../types';
import { useTeamStore } from '../store/teamStore';
import { useSubscription } from '../hooks/useSubscription';
import { 
  Shield,
  Lock
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { 
  FOOTBALL_OFFENSIVE_FORMATIONS, 
  FOOTBALL_DEFENSIVE_FORMATIONS,
  SOCCER_FORMATIONS
} from '../data/sportsFormations';
import UpgradeModal from './UpgradeModal';

// Import sport-specific icons
import FootballIcon from '../assets/icons/football.svg';
import BasketballIcon from '../assets/icons/basketball.svg';
import SoccerIcon from '../assets/icons/soccer.svg';
import BaseballIcon from '../assets/icons/baseball.svg';
import SoftballIcon from '../assets/icons/softball.svg';
import VolleyballIcon from '../assets/icons/volleyball.svg';
import HockeyIcon from '../assets/icons/hockey.svg';
import LacrosseIcon from '../assets/icons/lacrosse.svg';
import TennisIcon from '../assets/icons/tennis.svg';

const TeamSetup: React.FC = () => {
  const navigate = useNavigate();
  const createTeam = useTeamStore((state) => state.createTeam);
  const fetchAllTeams = useTeamStore((state) => state.fetchAllTeams);
  const allTeams = useTeamStore((state) => state.allTeams);
  const { isProUser, subscribeToPro } = useSubscription();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Fetch teams on component mount
  useEffect(() => {
    fetchAllTeams();
  }, [fetchAllTeams]);

  const [team, setTeam] = useState<Partial<Team>>({
    name: '',
    sport: 'Football',
    rosterSize: 22,
    ageLevel: '13U',
    experienceLevel: 'Intermediate',
    offensiveScheme: FOOTBALL_OFFENSIVE_FORMATIONS[0].name || 'Spread',
    defensiveScheme: FOOTBALL_DEFENSIVE_FORMATIONS[0].name || '4-3',
    defaultPracticeStartTime: '15:00',
    defaultPracticeEndTime: '17:00',
    defaultPracticeLocation: 'Main Field',
    timeFormat: '12h',
    playerSortBy: 'firstName',
    format: '11v11', // Default format for Football
  });

  const sports: Sport[] = ['Football', 'FlagFootball', 'Basketball', 'Soccer', 'Baseball', 'Softball', 'Volleyball', 'Hockey', 'Lacrosse', 'Tennis'];
  const experienceLevels: ExperienceLevel[] = ['Beginner', 'Intermediate', 'Advanced'];
  
  // Football schemes - use actual formation names
  const footballOffensiveSchemes = FOOTBALL_OFFENSIVE_FORMATIONS.map(formation => formation.name).sort();
  const footballDefensiveSchemes = FOOTBALL_DEFENSIVE_FORMATIONS.map(formation => formation.name).sort();

  // Basketball schemes
  const basketballOffensiveSchemes: BasketballOffensiveScheme[] = [
    'Motion', 'Princeton', 'Triangle', 'Pick and Roll', 'Fast Break', 'Flex', 'Zone'
  ];
  const basketballDefensiveSchemes: BasketballDefensiveScheme[] = [
    'Man-to-Man', '2-3 Zone', '3-2 Zone', '1-3-1 Zone', 'Box and One', 'Full Court Press', 'Half Court Press'
  ];

  // Soccer schemes - use actual formation names
  const soccerFormations = SOCCER_FORMATIONS.map(formation => formation.name).sort();
  const soccerStyles: SoccerStyle[] = [
    'Possession', 'Counter-Attack', 'High Press', 'Tiki-Taka', 'Direct', 'Total Football', 'Park the Bus'
  ];

  // Baseball/Softball focuses
  const baseballFocuses: BaseballFocus[] = [
    'Small Ball', 'Power Hitting', 'Defensive', 'Balanced', 'Pitching Focus', 'Speed Focus'
  ];

  // Hockey systems
  const hockeySystems: HockeySystem[] = [
    'Dump and Chase', 'Neutral Zone Trap', 'Left Wing Lock', '1-2-2 Forecheck', '2-1-2 Forecheck'
  ];

  // Volleyball schemes
  const volleyballOffenses: VolleyballOffense[] = [
    '5-1', '6-2', '4-2', 'International', 'West Coast'
  ];
  const volleyballDefenses: VolleyballDefense[] = [
    'Rotation', 'Perimeter', 'Man-Up', '6-Up', 'Base'
  ];

  // Lacrosse schemes
  const lacrosseOffenses: LacrosseOffense[] = [
    'Motion', '1-4-1', '2-3-1', '3-3', 'Wheel'
  ];
  const lacrosseDefenses: LacrosseDefense[] = [
    'Man-to-Man', 'Zone', 'Backer', 'Slide Package', 'Crease Defense'
  ];

  // Tennis playing styles
  const tennisStyles: TennisStyle[] = [
    'Baseline', 'Serve-and-Volley', 'All-Court', 'Counterpuncher', 'Aggressive Baseline'
  ];

  // Set reasonable defaults when sport changes
  useEffect(() => {
    const updatedTeam: Partial<Team> = { ...team };
    
    // Set reasonable roster size and format based on sport
    switch (team.sport) {
      case 'Football':
        updatedTeam.rosterSize = 22;
        updatedTeam.format = '11v11';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = footballOffensiveSchemes[0] || 'Spread';
        if (!team.defensiveScheme) updatedTeam.defensiveScheme = footballDefensiveSchemes[0] || '4-3';
        break;
      case 'FlagFootball':
        updatedTeam.rosterSize = 14;
        updatedTeam.format = '7v7';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = footballOffensiveSchemes[0] || 'Spread';
        if (!team.defensiveScheme) updatedTeam.defensiveScheme = 'Man Coverage';
        break;
      case 'Basketball':
        updatedTeam.rosterSize = 12;
        updatedTeam.format = '5v5';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = 'Motion';
        if (!team.defensiveScheme) updatedTeam.defensiveScheme = 'Man-to-Man';
        break;
      case 'Soccer':
        updatedTeam.rosterSize = 18;
        updatedTeam.format = '11v11';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = soccerFormations[0] || '4-4-2';
        if (!team.defensiveScheme) updatedTeam.defensiveScheme = 'Possession';
        break;
      case 'Baseball':
        updatedTeam.rosterSize = 15;
        updatedTeam.format = '9v9';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = 'Balanced';
        break;
      case 'Softball':
        updatedTeam.rosterSize = 15;
        updatedTeam.format = '10v10';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = 'Balanced';
        break;
      case 'Hockey':
        updatedTeam.rosterSize = 20;
        updatedTeam.format = '6v6';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = 'Dump and Chase';
        break;
      case 'Volleyball':
        updatedTeam.rosterSize = 12;
        updatedTeam.format = '6v6';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = '5-1';
        if (!team.defensiveScheme) updatedTeam.defensiveScheme = 'Rotation';
        break;
      case 'Lacrosse':
        updatedTeam.rosterSize = 20;
        updatedTeam.format = '10v10';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = 'Motion';
        if (!team.defensiveScheme) updatedTeam.defensiveScheme = 'Man-to-Man';
        break;
      case 'Tennis':
        updatedTeam.rosterSize = 12;
        updatedTeam.format = 'Singles';
        if (!team.offensiveScheme) updatedTeam.offensiveScheme = 'Baseline';
        break;
    }
    
    setTeam(updatedTeam);
  }, [team.sport]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Check if user is on rookie plan and already has a team
      if (!isProUser && allTeams.length >= 1) {
        setShowUpgradeModal(true);
        setIsSubmitting(false);
        return;
      }

      await createTeam(team as Omit<Team, 'id' | 'players'>);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpgrade = async () => {
    setShowUpgradeModal(false);
    await subscribeToPro();
  };

  // Helper function to get sport icon
  const getSportIcon = (sport: Sport) => {
    switch (sport) {
      case 'Football': 
        return <img src={FootballIcon} alt="Football" className="h-5 w-5" />;
      case 'Basketball': 
        return <img src={BasketballIcon} alt="Basketball" className="h-5 w-5" />;
      case 'Soccer': 
        return <img src={SoccerIcon} alt="Soccer" className="h-5 w-5" />;
      case 'Baseball': 
        return <img src={BaseballIcon} alt="Baseball" className="h-5 w-5" />;
      case 'Softball': 
        return <img src={SoftballIcon} alt="Softball" className="h-5 w-5" />;
      case 'Volleyball': 
        return <img src={VolleyballIcon} alt="Volleyball" className="h-5 w-5" />;
      case 'Hockey': 
        return <img src={HockeyIcon} alt="Hockey" className="h-5 w-5" />;
      case 'Lacrosse':
        return <img src={LacrosseIcon} alt="Lacrosse" className="h-5 w-5" />;
      case 'Tennis':
        return <img src={TennisIcon} alt="Tennis" className="h-5 w-5" />;
      case 'FlagFootball':
        return <img src={FootballIcon} alt="Flag Football" className="h-5 w-5" />;
      default: 
        return <Shield className="h-5 w-5" />;
    }
  };

  // Helper function to get display name for sports
  const getSportDisplayName = (sport: Sport): string => {
    if (sport === 'FlagFootball') return 'Flag Football';
    if (sport === 'Football') return 'Tackle Football';
    return sport;
  };

  const toOpts = (arr: string[], placeholder: string) => [
    { value: '', label: placeholder },
    ...arr.map((v) => ({ value: v, label: v })),
  ];

  const renderSportSpecificFields = () => {
    switch (team.sport) {
      case 'Football':
      case 'FlagFootball':
        return (
          <>
            <div>
              <label className="form-label">Offensive Scheme</label>
              <MobileSelect
                className="select"
                value={team.offensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, offensiveScheme: v as FootballOffensiveScheme })}
                options={toOpts(footballOffensiveSchemes, 'Select Offensive Scheme')}
                aria-label="Offensive Scheme"
                sheetTitle="Offensive Scheme"
              />
            </div>
            <div>
              <label className="form-label">Defensive Scheme</label>
              <MobileSelect
                className="select"
                value={team.defensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, defensiveScheme: v as FootballDefensiveScheme })}
                options={toOpts(footballDefensiveSchemes, 'Select Defensive Scheme')}
                aria-label="Defensive Scheme"
                sheetTitle="Defensive Scheme"
              />
            </div>
          </>
        );

      case 'Basketball':
        return (
          <>
            <div>
              <label className="form-label">Offensive Scheme</label>
              <MobileSelect
                className="select"
                value={team.offensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, offensiveScheme: v as BasketballOffensiveScheme })}
                options={toOpts(basketballOffensiveSchemes, 'Select Offensive Scheme')}
                aria-label="Offensive Scheme"
                sheetTitle="Offensive Scheme"
              />
            </div>
            <div>
              <label className="form-label">Defensive Scheme</label>
              <MobileSelect
                className="select"
                value={team.defensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, defensiveScheme: v as BasketballDefensiveScheme })}
                options={toOpts(basketballDefensiveSchemes, 'Select Defensive Scheme')}
                aria-label="Defensive Scheme"
                sheetTitle="Defensive Scheme"
              />
            </div>
          </>
        );

      case 'Soccer':
        return (
          <>
            <div>
              <label className="form-label">Formation</label>
              <MobileSelect
                className="select"
                value={team.offensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, offensiveScheme: v as SoccerFormation })}
                options={toOpts(soccerFormations, 'Select Formation')}
                aria-label="Formation"
                sheetTitle="Formation"
              />
            </div>
            <div>
              <label className="form-label">Playing Style</label>
              <MobileSelect
                className="select"
                value={team.defensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, defensiveScheme: v as SoccerStyle })}
                options={toOpts(soccerStyles, 'Select Playing Style')}
                aria-label="Playing Style"
                sheetTitle="Playing Style"
              />
            </div>
          </>
        );

      case 'Baseball':
      case 'Softball':
        return (
          <div>
            <label className="form-label">Team Focus</label>
            <MobileSelect
              className="select"
              value={team.offensiveScheme || ''}
              onChange={(v) => setTeam({ ...team, offensiveScheme: v as BaseballFocus })}
              options={toOpts(baseballFocuses, 'Select Team Focus')}
              aria-label="Team Focus"
              sheetTitle="Team Focus"
            />
          </div>
        );

      case 'Hockey':
        return (
          <div>
            <label className="form-label">Team System</label>
            <MobileSelect
              className="select"
              value={team.offensiveScheme || ''}
              onChange={(v) => setTeam({ ...team, offensiveScheme: v as HockeySystem })}
              options={toOpts(hockeySystems, 'Select Team System')}
              aria-label="Team System"
              sheetTitle="Team System"
            />
          </div>
        );

      case 'Volleyball':
        return (
          <>
            <div>
              <label className="form-label">Offensive System</label>
              <MobileSelect
                className="select"
                value={team.offensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, offensiveScheme: v as VolleyballOffense })}
                options={toOpts(volleyballOffenses, 'Select Offensive System')}
                aria-label="Offensive System"
                sheetTitle="Offensive System"
              />
            </div>
            <div>
              <label className="form-label">Defensive System</label>
              <MobileSelect
                className="select"
                value={team.defensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, defensiveScheme: v as VolleyballDefense })}
                options={toOpts(volleyballDefenses, 'Select Defensive System')}
                aria-label="Defensive System"
                sheetTitle="Defensive System"
              />
            </div>
          </>
        );

      case 'Lacrosse':
        return (
          <>
            <div>
              <label className="form-label">Offensive System</label>
              <MobileSelect
                className="select"
                value={team.offensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, offensiveScheme: v as LacrosseOffense })}
                options={toOpts(lacrosseOffenses, 'Select Offensive System')}
                aria-label="Offensive System"
                sheetTitle="Offensive System"
              />
            </div>
            <div>
              <label className="form-label">Defensive System</label>
              <MobileSelect
                className="select"
                value={team.defensiveScheme || ''}
                onChange={(v) => setTeam({ ...team, defensiveScheme: v as LacrosseDefense })}
                options={toOpts(lacrosseDefenses, 'Select Defensive System')}
                aria-label="Defensive System"
                sheetTitle="Defensive System"
              />
            </div>
          </>
        );

      case 'Tennis':
        return (
          <div>
            <label className="form-label">Playing Style</label>
            <MobileSelect
              className="select"
              value={team.offensiveScheme || ''}
              onChange={(v) => setTeam({ ...team, offensiveScheme: v as TennisStyle })}
              options={toOpts(tennisStyles, 'Select Playing Style')}
              aria-label="Playing Style"
              sheetTitle="Playing Style"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="card">
      <div className="flex items-center mb-6">
        <Shield className="h-8 w-8 text-primary mr-2" />
        <h2 className="text-h3">Team Setup</h2>
      </div>
      
      {/* Subscription Status */}
      {!isProUser && allTeams.length >= 1 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl p-4 mb-6">
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="font-medium">Rookie Plan Limit Reached</p>
              <p className="text-sm text-yellow-700">
                You can create up to 1 team on the Rookie plan. Upgrade to Pro for unlimited teams, unlimited AI Coach, lineup optimization, and more.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="form-label">
            Team Name
          </label>
          <input
            type="text"
            className="input"
            value={team.name || ''}
            onChange={(e) => setTeam({ ...team, name: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="form-label">
            Sport
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {sports.map((sport) => (
              <button
                key={sport}
                type="button"
                onClick={() => setTeam({ ...team, sport })}
                className={`flex items-center justify-center px-4 py-2 rounded-xl border ${
                  team.sport === sport
                    ? 'bg-secondary-hover border-primary text-primary font-semibold'
                    : 'bg-white border-stone text-dark hover:bg-cream'
                }`}
              >
                <span className="mr-2">{getSportIcon(sport)}</span>
                {getSportDisplayName(sport)}
              </button>
            ))}
          </div>
        </div>

        {/* Format Selection */}
        {team.sport && SportFormats[team.sport] && (
          <div>
            <label className="form-label">
              Format
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {SportFormats[team.sport].map((format) => (
                <button
                  key={format}
                  type="button"
                  onClick={() => setTeam({ ...team, format })}
                  className={`flex items-center justify-center px-4 py-2 rounded-xl border ${
                    team.format === format
                      ? 'bg-secondary-hover border-primary text-primary font-semibold'
                      : 'bg-white border-stone text-dark hover:bg-cream'
                  }`}
                >
                  {format}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="form-label">
            Roster Size
          </label>
          <input
            type="number"
            min="1"
            className="input"
            value={team.rosterSize || ''}
            onChange={(e) => setTeam({ ...team, rosterSize: parseInt(e.target.value) })}
            required
          />
        </div>

        <div>
          <label className="form-label">Age Level</label>
          <MobileSelect
            className="select"
            value={team.ageLevel || ''}
            onChange={(v) => setTeam({ ...team, ageLevel: v as AgeLevel })}
            options={[
              { value: '', label: 'Select Age Level' },
              ...AGE_LEVELS.map((l) => ({ value: l, label: getAgeLevelLabel(l) })),
            ]}
            aria-label="Age Level"
            sheetTitle="Age Level"
          />
        </div>

        <div>
          <label className="form-label">Experience Level</label>
          <MobileSelect
            className="select"
            value={team.experienceLevel || ''}
            onChange={(v) => setTeam({ ...team, experienceLevel: v as ExperienceLevel })}
            options={[
              { value: '', label: 'Select Experience Level' },
              ...experienceLevels.map((l) => ({ value: l, label: l })),
            ]}
            aria-label="Experience Level"
            sheetTitle="Experience Level"
          />
        </div>

        {/* Render sport-specific fields */}
        {renderSportSpecificFields()}

        <div className="mt-8">
          <h3 className="text-h4 mb-4">Practice Preferences (Optional)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                Default Start Time
              </label>
              <NativeTimeInput
                className="input"
                value={team.defaultPracticeStartTime || '15:00'}
                onChange={(e) => setTeam({ ...team, defaultPracticeStartTime: e.target.value })}
              />
            </div>

            <div>
              <label className="form-label">
                Default End Time
              </label>
              <NativeTimeInput
                className="input"
                value={team.defaultPracticeEndTime || '17:00'}
                onChange={(e) => setTeam({ ...team, defaultPracticeEndTime: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <label className="form-label">
                Default Practice Location
              </label>
              <input
                type="text"
                className="input"
                placeholder="e.g. Main Field"
                value={team.defaultPracticeLocation || ''}
                onChange={(e) => setTeam({ ...team, defaultPracticeLocation: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-h4 mb-4">Display Settings</h3>
          <div>
            <label className="form-label">Time Format</label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => setTeam({ ...team, timeFormat: '12h' })}
                className={`px-4 py-2 rounded-xl border ${
                  team.timeFormat !== '24h'
                    ? 'bg-secondary-hover border-primary text-primary font-semibold'
                    : 'bg-white border-stone text-dark'
                }`}
              >
                12-hour (1:00 PM)
              </button>
              <button
                type="button"
                onClick={() => setTeam({ ...team, timeFormat: '24h' })}
                className={`px-4 py-2 rounded-xl border ${
                  team.timeFormat === '24h'
                    ? 'bg-secondary-hover border-primary text-primary font-semibold'
                    : 'bg-white border-stone text-dark'
                }`}
              >
                24-hour (13:00)
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-h4 mb-4">Feature Settings</h3>
          <div className="bg-secondary-hover rounded-xl p-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                checked={team.enableRandomization || false}
                onChange={(e) => setTeam({ ...team, enableRandomization: e.target.checked })}
              />
              <div className="flex-1">
                <span className="font-medium text-dark">Enable Position Randomization</span>
                <p className="text-sm text-gray-600 mt-1">
                  Add a randomization button to depth charts for quickly assigning players to positions. 
                  Great for {team.sport === 'Baseball' || team.sport === 'Softball' 
                    ? 'rotating fielding positions across innings' 
                    : 'practice lineups and scrimmages'}.
                </p>
              </div>
            </label>
          </div>
        </div>

        {!isProUser && allTeams.length >= 1 ? (
          <button
            type="button"
            onClick={() => setShowUpgradeModal(true)}
            className="w-full py-3 text-lg rounded-xl border border-gray-300 bg-gray-100 text-gray-500 flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
          >
            <Lock className="h-4 w-4" />
            Create Team
          </button>
        ) : (
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full py-3 text-lg"
          >
            {isSubmitting ? 'Creating Team...' : 'Create Team'}
          </button>
        )}
      </form>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
};

export default TeamSetup;